import { Icon, Popover } from '@sorrem-client/ui/components';
import classnames from 'classnames';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import styles from './OptionTile.module.scss';

/** How long the pointer (or keyboard focus) rests on a tile before its larger preview shows. */
const PREVIEW_DELAY_MS = 1000;

interface OptionTileProps {
    label: string;
    /**
     * Draws the choice's image with the given class, once in the tile and
     * once, larger, in the hover preview. Returns null while there is no
     * image yet, which leaves the frame empty and shows no preview.
     */
    renderImage: (className: string | undefined) => ReactNode;
    /** Shown under the name in the hover preview, e.g. an item's stats. */
    previewDetails?: ReactNode;
    /** Whether the current image is still on its way: shows a loading icon over the frame (and any previous image). */
    isLoading?: boolean;
    isSelected: boolean;
    isDisabled: boolean;
    onClick: () => void;
}

/**
 * One choice in a character-creation picker: its image clipped to a thin
 * frame and captioned with its name. The selected choice's frame is
 * highlighted. Hovering or focusing it for `PREVIEW_DELAY_MS` shows a larger
 * preview beside it, once it has an image, so passing the pointer across a
 * grid doesn't flash previews.
 */
export function OptionTile({ label, renderImage, previewDetails, isLoading = false, isSelected, isDisabled, onClick }: OptionTileProps): JSX.Element {
    const tileRef = useRef<HTMLButtonElement>(null);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const cancelPreviewTimer = (): void => {
        if (previewTimerRef.current !== null) {
            clearTimeout(previewTimerRef.current);
            previewTimerRef.current = null;
        }
    };
    const openPreview = (): void => {
        cancelPreviewTimer();
        previewTimerRef.current = setTimeout(() => {
            previewTimerRef.current = null;
            setIsPreviewOpen(true);
        }, PREVIEW_DELAY_MS);
    };
    const closePreview = (): void => {
        cancelPreviewTimer();
        setIsPreviewOpen(false);
    };
    // A tile removed mid-wait (e.g. on a gender switch) must not open a preview afterward.
    useEffect(() => cancelPreviewTimer, []);

    const image = renderImage(styles.thumbnail);
    const previewImage = isPreviewOpen ? renderImage(styles.previewImage) : null;

    return (
        <button
            ref={tileRef}
            type="button"
            className={classnames(styles.tile, isSelected && styles.selected)}
            disabled={isDisabled}
            onClick={onClick}
            onMouseEnter={openPreview}
            onMouseLeave={closePreview}
            onFocus={openPreview}
            onBlur={closePreview}
            aria-pressed={isSelected}
        >
            <span className={classnames(styles.portrait, isLoading && styles.loading)}>
                {image}
                {isLoading && <Icon icon="loading" spin="fast" className={styles.spinner} tooltip="Loading" />}
            </span>
            <span className={styles.label}>{label}</span>
            {/* Opens toward the 3D scene, away from the panel at the screen's right edge. */}
            <Popover open={previewImage !== null} anchorRef={tileRef} placement="left" onClose={closePreview}>
                <span className={styles.preview}>
                    {previewImage}
                    <span className={styles.previewLabel}>{label}</span>
                    {previewDetails && <span className={styles.previewDetails}>{previewDetails}</span>}
                </span>
            </Popover>
        </button>
    );
}
